import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { useSiteSettings } from "@/lib/SiteSettingsContext";
import { CTA } from "@/lib/ctas";

const NAV = [
    { to: "/", label: "Home", end: true },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/case-studies", label: "Case Studies" },
    { to: "/pricing", label: "Pricing" },
    { to: "/blog", label: "Blog" },
    { to: "/contact", label: "Contact" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { settings } = useSiteSettings();
    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <header data-testid="public-header" className="h-nav sticky top-0 z-30">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo">
                    <motion.span
                        className="w-9 h-9 rounded-full grid place-items-center text-white font-display font-bold text-lg"
                        style={{ background: "var(--grad-btn)", boxShadow: "0 0 18px -2px rgba(124,107,255,0.6)" }}
                        whileHover={{ rotate: 15 }}
                        transition={{ type: "spring", stiffness: 300, damping: 12 }}
                    >Ω</motion.span>
                    <span className="font-display font-bold text-[15px] tracking-tight" style={{ color: "var(--text)" }}>
                        {settings.company_name || "Omega IT"}
                    </span>
                </Link>
                <nav className="hidden lg:flex items-center gap-1">
                    {NAV.map((n) => (
                        <NavLink key={n.to} to={n.to} end={n.end} data-testid={`header-nav-${n.to.replace(/\//g, "-").replace(/^-/, "") || "home"}`}
                            className={({ isActive }) => `px-3 py-2 rounded-md text-sm font-medium nav-link ${isActive ? "" : ""}`}
                            style={({ isActive }) => ({ color: isActive ? "var(--text)" : "var(--muted)" })}>
                            {n.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="flex items-center gap-2">
                    <Link to={CTA.CONTACT.href} className="hidden lg:inline-flex btn-primary" data-testid="header-cta-button">
                        {CTA.CONTACT.label} <ArrowUpRight className="w-4 h-4" />
                    </Link>
                    <button type="button" className="lg:hidden w-10 h-10 grid place-items-center rounded-md" style={{ color: "var(--text)" }} onClick={() => setOpen((v) => !v)} aria-label={open ? "Close menu" : "Open menu"} data-testid="header-mobile-toggle">
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>
            {open && (
                <div className="lg:hidden" style={{ borderTop: "1px solid var(--line-soft)", background: "var(--ink)" }} data-testid="header-mobile-menu">
                    <div className="px-5 sm:px-8 py-4 flex flex-col gap-1">
                        {NAV.map((n) => (
                            <NavLink key={n.to} to={n.to} end={n.end}
                                className={({ isActive }) => `px-3 py-3 rounded-md text-sm font-semibold`}
                                style={({ isActive }) => ({ color: isActive ? "var(--text)" : "var(--muted)", background: isActive ? "var(--surface)" : "transparent" })}>
                                {n.label}
                            </NavLink>
                        ))}
                        <Link to={CTA.CONTACT.href} className="btn-primary mt-3">{CTA.CONTACT.label} <ArrowUpRight className="w-4 h-4" /></Link>
                    </div>
                </div>
            )}
        </header>
    );
}
